import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { useVisitor } from "../context/VisitorAuthContext";
import { TV, TvKicker, TvHeader } from "../components/TriviaTheme";
import { readStreak } from "../lib/streak";
import { isFestive, XMAS_URL } from "../lib/season";
import { ChartRounds } from "../components/ChartRounds";
import { SportingMoments } from "../components/SportingMoments";
import { SportThreeCard } from "../components/SportThreeCard";
import { DatasetRounds } from "../components/DatasetRounds";
import {
  Brain, Clapperboard, Scroll, Coffee, Newspaper, Atom, Trophy, Globe2,
  Sparkles, Users, ListChecks, PenLine, Archive, ArrowRight, Star,
} from "lucide-react";

const CAT_ICON = {
  Culture: Brain, Entertainment: Clapperboard, History: Scroll, Lifestyle: Coffee,
  Media: Newspaper, Sciences: Atom, "Sport & Games": Trophy, World: Globe2,
};

const Pill = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-ui text-xs sm:text-[13px] font-semibold transition-[background-color,color,transform] active:scale-[0.97] ${
      active ? "bg-[#161514] text-white border border-[#161514]" : "bg-[#EEE8DD] text-[#4A453E] border border-[#DDD5C7] hover:bg-[#E6DFD2]"
    }`}
  >
    {Icon && <Icon className="w-3.5 h-3.5" />} {label}
  </button>
);

const StatCard = ({ label, value, note, green, testid, delay }) => (
  <div data-testid={testid} style={{ animationDelay: delay }} className={`tv-rise p-3.5 sm:p-5 min-h-[112px] sm:min-h-[132px] flex flex-col min-w-0 ${green ? TV.green : TV.card}`}>
    <p className={`tv-caps !text-[9px] sm:!text-[10px] !tracking-[0.12em] truncate ${green ? "text-white/75" : "text-[#57524B]"}`}>{label}</p>
    <p className={`font-ui font-bold mt-2.5 leading-[1.15] ${green ? "text-sm sm:text-base" : "text-2xl sm:text-3xl tracking-tight text-[#161514] tabular-nums"}`}>{value}</p>
    <p className={`font-ui text-[11px] sm:text-xs mt-auto pt-2 ${green ? "text-white/70" : "text-[#77716A]"}`}>{note}</p>
  </div>
);

export default function Trivia() {
  const [stats, setStats] = useState(null);
  const [cats, setCats] = useState([]);
  const [mode, setMode] = useState("mc");
  const [group, setGroup] = useState(false);
  const [difficulty, setDifficulty] = useState(0);
  const nav = useNavigate();
  const { isMember, requireMember } = useVisitor();
  const [streak] = useState(readStreak);

  const toggleGroup = () => {
    if (!group && !requireMember({ title: "Host mode is Club-only", message: "Run the room as quiz host with CurioVault Club — plus unlimited quizzes and the full archive." })) return;
    setGroup((g) => !g);
  };
  useEffect(() => {
    api.get("/trivia/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/trivia/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const q = (extra) => new URLSearchParams({ mode, group: group ? "1" : "0", ...extra }).toString();

  return (
    <Layout>
      <div className={`font-ui ${TV.bg} min-h-screen`}>
        <TvHeader title="Trivia Vault" subtitle="Pub-quiz rounds · 8 categories" isMember={isMember} />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-24 sm:pb-16 pad-when-cookie">
          <TvKicker className="tv-rise">For: pub quiz players</TvKicker>
          <h1 className="tv-rise font-tv-serif font-bold text-[#161514] text-[34px] sm:text-5xl leading-[1.02] mt-4" style={{ animationDelay: "60ms" }}>
            Think you know<br />your stuff.
          </h1>
          <p className="tv-rise font-ui text-[15px] sm:text-lg text-[#77716A] mt-3 max-w-xl leading-relaxed" style={{ animationDelay: "120ms" }}>
            A daily three, eight categories, and a host mode for the whole room.
          </p>

          {isFestive() && (
            <button data-testid="hub-christmas-feature" onClick={() => nav(XMAS_URL)}
              className={`${TV.green} tv-rise w-full text-left mt-6 p-5 sm:p-6 relative overflow-hidden flex items-center justify-between gap-4 border border-[#C5A059]/60 hover:-translate-y-0.5 transition-transform`}>
              <Star className="absolute -right-4 -top-4 w-28 h-28 text-[#C5A059]/25" fill="currentColor" />
              <div className="relative">
                <p className="tv-caps text-[#E8D5A6]">December special</p>
                <p className="font-ui font-bold text-lg sm:text-xl mt-1.5">Christmas No.1s: every one since 1952</p>
              </div>
              <span className="relative shrink-0 inline-flex items-center gap-1.5 bg-white text-[#161514] rounded-full px-4 py-2 font-ui text-sm font-semibold">Play <ArrowRight className="w-4 h-4" /></span>
            </button>
          )}

          <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-7">
            <StatCard testid="trivia-total-badge" label="In the vault" value={stats ? stats.total.toLocaleString() : "…"} note="Quiz questions" delay="160ms" />
            <StatCard label="Categories" value="8" note="IQA canon" delay="220ms" />
            <StatCard green testid="hub-streak-card" label={streak.count ? "Your streak" : "Today"}
              value={streak.count ? `${streak.count}-day streak` : "Daily Three is ready"}
              note={streak.count ? (streak.playedToday ? "Back tomorrow to keep it" : "Play today to keep it") : "New set every day"} delay="280ms" />
          </div>

          {/* Daily feature card */}
          <div className={`${TV.green} tv-rise p-5 sm:p-7 mt-5`} style={{ animationDelay: "320ms" }} data-testid="daily-card">
            <p className="tv-caps text-white/70">Daily Three</p>
            <h2 className="font-ui font-semibold text-xl sm:text-2xl leading-snug tracking-tight mt-3 max-w-xl">
              “Three questions. Three categories. Same set for everyone today.”
            </h2>
            <div className={`${TV.greenInset} p-4 sm:p-5 mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4`}>
              <ul className="font-ui text-white/85 space-y-1 text-[13px] sm:text-sm list-disc pl-4">
                <li>Free for everyone, every day</li>
                <li>45 seconds per question</li>
                <li>Compare scores with friends</li>
              </ul>
              <button
                data-testid="play-daily-btn"
                onClick={() => nav(`/trivia/play?${q({ source: "daily" })}`)}
                className="shrink-0 inline-flex items-center justify-center gap-2 bg-white text-[#161514] hover:bg-[#F4F1EA] active:scale-[0.98] font-ui font-semibold text-sm px-5 py-2.5 rounded-full transition-[transform,background-color]"
              >
                Play today <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Round settings */}
          <div className={`${TV.card} mt-5 overflow-hidden`}>
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-4">
                <h2 className="font-tv-serif font-bold text-xl sm:text-2xl text-[#161514] leading-tight">Set up your round</h2>
                <span className={`${TV.pillTag} tv-caps !text-[9.5px] px-3 py-1 shrink-0`}>{group ? "Host" : "Solo"}</span>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 sm:gap-5 mt-5">
                <div>
                  <p className="tv-caps !text-[11px] text-[#77716A] mb-3">Answer mode</p>
                  <div className="flex flex-wrap gap-2">
                    <Pill testid="mode-mc" active={mode === "mc"} onClick={() => setMode("mc")} icon={ListChecks} label="Multiple choice" />
                    <Pill testid="mode-purist" active={mode === "purist"} onClick={() => setMode("purist")} icon={PenLine} label="Purist" />
                  </div>
                </div>
                <div>
                  <p className="tv-caps !text-[11px] text-[#77716A] mb-3">Host mode</p>
                  <Pill testid="group-toggle" active={group} onClick={toggleGroup} icon={Users} label={group ? "Group mode ON" : "Group mode"} />
                </div>
                <div>
                  <p className="tv-caps !text-[11px] text-[#77716A] mb-3">Difficulty</p>
                  <div className="flex flex-wrap gap-2">
                    {[[0, "All"], [1, "Easy"], [2, "Medium"], [3, "Hard"]].map(([d, l]) => (
                      <Pill key={d} testid={`difficulty-pill-${d}`} active={difficulty === d} onClick={() => setDifficulty(d)} label={l} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className={`${TV.footer} px-5 sm:px-6 py-2.5 flex justify-between font-ui text-[11px] sm:text-xs text-[#77716A]`}>
              <span>Mode: {mode === "mc" ? "multiple choice" : "purist (type it)"}</span>
              <span>Difficulty: {["all", "easy", "medium", "hard"][difficulty]}</span>
            </div>
          </div>

          {/* Categories */}
          <div className="flex items-end justify-between mt-10 mb-4">
            <div>
              <TvKicker line={false}>Pick a category</TvKicker>
              <h2 className="font-tv-serif font-bold text-xl sm:text-2xl text-[#161514] mt-2">Ten-question rounds</h2>
            </div>
            <Link data-testid="archive-link" to="/trivia/archive" className={`${TV.pillLight} inline-flex items-center gap-1.5 px-3 py-1.5 font-ui text-xs font-semibold`}>
              <Archive className="w-3.5 h-3.5" /> Archive
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
            {cats.map((c, i) => {
              const Icon = CAT_ICON[c.category] || Brain;
              return (
                <button
                  key={c.category}
                  style={{ animationDelay: `${i * 40}ms` }}
                  data-testid={`category-${c.category.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => nav(`/trivia/play?${q({ source: "category", category: c.category, difficulty: String(difficulty), count: "10" })}`)}
                  className={`tv-rise group ${TV.card} !rounded-[18px] p-3.5 sm:p-4 text-left min-w-0 hover:-translate-y-0.5 hover:border-[#2D4A3E] transition-[transform,border-color]`}
                >
                  <span className="w-8 h-8 rounded-full bg-[#EEE8DD] group-hover:bg-[#2D4A3E] flex items-center justify-center transition-colors">
                    <Icon className="w-4 h-4 text-[#161514] group-hover:text-white transition-colors" />
                  </span>
                  <p className="font-ui text-sm sm:text-[15px] font-bold text-[#161514] leading-tight mt-3 truncate">{c.category}</p>
                  <p className="font-ui text-[11px] sm:text-xs text-[#77716A] mt-0.5">{c.count.toLocaleString()} questions</p>
                </button>
              );
            })}
          </div>

          <ChartRounds
            onPlay={(extra) => nav(`/trivia/play?${q({ source: "pack", count: "10", ...extra })}`)}
            onChartNight={(c) => { if (requireMember({ title: "Chart Night is Club-only", message: "Host a big-screen chart countdown for your group with CurioVault Club, plus unlimited quizzes." })) nav(`/trivia/chart-night?${new URLSearchParams(c)}`); }}
          />
          <DatasetRounds onPlay={(extra) => nav(`/trivia/play?${q({ source: "pack", count: "10", ...extra })}`)} />
          <SportThreeCard />
          <SportingMoments
            onPlay={(extra) => nav(`/trivia/play?${q({ source: "pack", count: "10", ...extra })}`)}
            onSportNight={() => { if (requireMember({ title: "Sport Night is Club-only", message: "Host a big-screen sport quiz for your group with CurioVault Club, plus unlimited quizzes." })) nav("/trivia/sport-night"); }}
          />

          <Link to="/trivia/archive" data-testid="archive-dashed-card" className="block mt-5 border-2 border-dashed border-[#D3CABB] rounded-[18px] p-4 sm:p-5 hover:bg-[#EFEBE2] transition-colors">
            <p className="font-ui font-bold text-sm sm:text-base text-[#161514]">Missed a day?</p>
            <p className="font-ui text-xs sm:text-sm text-[#77716A] mt-1">Replay any of the last 14 Daily Threes from the archive. Club members only.</p>
          </Link>
        </div>
        <TriviaBottomNav active="home" />
      </div>
    </Layout>
  );
}

export const TriviaBottomNav = ({ active }) => {
  const items = [
    { key: "home", label: "Quiz", icon: Trophy, to: "/trivia" },
    { key: "archive", label: "Archive", icon: Archive, to: "/trivia/archive" },
    { key: "club", label: "Club", icon: Sparkles, to: "/club" },
  ];
  return (
    <nav className="sm:hidden fixed bottom-0 left-0 right-0 bg-[#F4F1EA]/95 backdrop-blur-md border-t border-[#DDD5C7] z-40 px-4 py-2 flex items-center justify-around">
      {items.map((it) => {
        const Icon = it.icon;
        const on = active === it.key;
        return (
          <Link
            key={it.key}
            to={it.to}
            data-testid={`bottom-nav-${it.key}`}
            className={`flex flex-col items-center gap-0.5 py-1 px-4 rounded-full text-[11px] font-semibold transition-colors ${on ? "text-[#2D4A3E] bg-[#E5EEE8]" : "text-[#77716A] hover:text-[#161514]"}`}
          >
            <Icon className="w-4 h-4" /> {it.label}
          </Link>
        );
      })}
    </nav>
  );
};
